import { TFunction } from 'i18next'

import { KEYLESS_PROVIDERS } from '@/constants/keyless'
import { Building } from '@/reducers/types'
import { isProductionBuild } from '@/utils/environment'
import { formatPhoneNumberDisplay } from '@/utils/phone-number'

import { filterBySearch, sortItems } from '../shared/person-table.utils'
import { PLACE_FEATURES, PlaceFeatureKey } from './features/places-features.constants'
import { getBuildingPms, PlacePmsType, PMS_LABELS, PMS_NONE } from './places.pms'
import { PlaceSortKey } from './places.sort-keys'
import { PLACE_STATUS_SEVERITY, PlaceStatus, PlaceStatusFilter } from './places.status-filter'
import { PLACE_TIMEZONES } from './places.timezones'

export const getPlaceStatus = (building: Building): PlaceStatus => {
  if (building.accountSuspended) return PlaceStatus.suspended
  if (building.accountPastDue) return PlaceStatus.delinquent
  return building.active === false ? PlaceStatus.inactive : PlaceStatus.active
}

export const getKeylessProviderNames = (building: Building) =>
  building.keylessAccessAvailable
    ? KEYLESS_PROVIDERS.filter(({ flag }) => building[flag]).map(({ name }) => name)
    : []

// Note: account switches belong to the status column, hidden ones are derived values
export const getEnabledFeatureKeys = (building: Building): PlaceFeatureKey[] =>
  PLACE_FEATURES.filter(
    (feature) =>
      feature.group !== 'account' &&
      !('hidden' in feature && feature.hidden) &&
      building[feature.key],
  ).map(({ key }) => key)

export const getTimezoneLabel = (timezone: string | undefined) => {
  if (!timezone) return null
  const short = Object.entries(PLACE_TIMEZONES).find(([, value]) => value === timezone)?.[0]
  return short ?? timezone
}

export const getPlaceAddress = (building: Building) => {
  const locality = [building.city, [building.state, building.postalCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')
  return { locality, street: building.address ?? '' }
}

// Note: the legacy building list is untyped - codes and postal codes can arrive as numbers
const asText = (value: unknown) => (value === null || value === undefined ? '' : String(value))

export const getPlaceName = (building: Building) => asText(building.name ?? building.text)

export const filterPlaces = (places: Building[], search?: string) =>
  filterBySearch(
    places,
    search,
    (building) => [
      getPlaceName(building),
      asText(building.code),
      asText(building.address),
      asText(building.city),
      asText(building.state),
      asText(building.postalCode),
    ],
    (building) => building.phone,
  )

export const filterPlacesByStatus = (places: Building[], status?: PlaceStatusFilter) =>
  !status || status === PlaceStatusFilter.all
    ? places
    : places.filter((building) => getPlaceStatus(building) === status)

export const filterPlacesByPms = (
  places: Building[],
  pmsTypes?: Array<PlacePmsType | typeof PMS_NONE>,
) => {
  if (!pmsTypes?.length) return places

  return places.filter((building) => pmsTypes.includes(getBuildingPms(building) ?? PMS_NONE))
}

export const filterPlacesByFeature = (places: Building[], features?: string[]) =>
  features?.length
    ? places.filter((building) => features.some((feature) => !!building[feature]))
    : places

export const filterPlacesByTags = (places: Building[], tags?: string[]) => {
  if (!tags?.length) return places

  return places.filter((building) => {
    const buildingTags = (building.tags ?? []).map(String)
    return tags.some((tag) => buildingTags.includes(tag))
  })
}

const getSortValue = (building: Building, sortBy: PlaceSortKey) => {
  switch (sortBy) {
    case PlaceSortKey.accessControl:
      return getKeylessProviderNames(building).length
    case PlaceSortKey.address:
      return asText(building.address).toLowerCase()
    case PlaceSortKey.createdAt:
      return building.createdAt ? new Date(building.createdAt).getTime() : 0
    case PlaceSortKey.features:
      return getEnabledFeatureKeys(building).length
    case PlaceSortKey.name:
      return getPlaceName(building).toLowerCase()
    case PlaceSortKey.phone:
      return asText(building.phone).replace(/\D/g, '')
    case PlaceSortKey.pms:
      return getBuildingPms(building) ?? ''
    case PlaceSortKey.status:
      return PLACE_STATUS_SEVERITY[getPlaceStatus(building)]
    case PlaceSortKey.timezone:
      return getTimezoneLabel(building.timezone) ?? ''
  }
}

export const sortPlaces = (
  places: Building[],
  sortBy?: PlaceSortKey | null,
  sortOrder?: 'asc' | 'desc' | null,
) => sortItems(places, sortBy, sortOrder, getSortValue)

export const getPlaceTags = (places: Building[]) =>
  [...new Set(places.flatMap((building) => (building.tags ?? []).map(String)))].sort((a, b) =>
    a.localeCompare(b),
  )

export const getPlacesCsvFields = (t: TFunction<['organisation']>) => [
  { label: t('organisation:places.table.csv.name'), value: (row: Building) => getPlaceName(row) },
  { label: t('organisation:places.table.csv.code'), value: (row: Building) => row.code ?? '' },
  {
    label: t('organisation:places.table.csv.address'),
    value: (row: Building) => row.address ?? '',
  },
  { label: t('organisation:places.table.csv.city'), value: (row: Building) => row.city ?? '' },
  { label: t('organisation:places.table.csv.state'), value: (row: Building) => row.state ?? '' },
  {
    label: t('organisation:places.table.csv.postalCode'),
    value: (row: Building) => row.postalCode ?? '',
  },
  {
    label: t('organisation:places.table.csv.timezone'),
    value: (row: Building) => getTimezoneLabel(row.timezone) ?? '',
  },
  {
    label: t('organisation:places.table.csv.phone'),
    value: (row: Building) => formatPhoneNumberDisplay(row.phone),
  },
  {
    label: t('organisation:places.table.csv.status'),
    value: (row: Building) => t(`organisation:places.statuses.${getPlaceStatus(row)}`),
  },
  {
    label: t('organisation:places.table.csv.pms'),
    value: (row: Building) => {
      const pms = getBuildingPms(row)
      return pms ? PMS_LABELS[pms] : ''
    },
  },
  {
    label: t('organisation:places.table.csv.accessControl'),
    value: (row: Building) => getKeylessProviderNames(row).join(', '),
  },
  {
    label: t('organisation:places.table.csv.features'),
    value: (row: Building) =>
      getEnabledFeatureKeys(row)
        .map((key) => t(`organisation:places.building.toggles.${key}`))
        .join(', '),
  },
  {
    label: t('organisation:places.table.csv.createdAt'),
    value: (row: Building) =>
      row.createdAt ? new Date(row.createdAt).toISOString().slice(0, 10) : '',
  },
]

// Note: the backend only accepts a bare 10-digit number in production; elsewhere it wants the country code
export const toBuildingPhone = (digits: string) =>
  isProductionBuild || digits.length !== 10 ? digits : `1${digits}`

export const isCanadaCountry = (country?: string | null) => country?.toUpperCase() === 'CA'
